import { Action, createReducer, on } from "@ngrx/store";
import * as invoicesActions from "./invoices.actions";
import { InvoiceDto } from "../interfaces/invoice_dto";
import { InvoiceItemDto } from "../interfaces/invoice_item_dto";
import * as errorActions from "../../core/error/ngrx/error.actions";

export const invoiceFeatureKey = "invoices";

export interface State {
    [invoiceFeatureKey]: InvoicesState;
    areInvoicesLoaded: boolean;
    selectedInvoice: number | null;
    isLoading: boolean;
    isVisible: boolean;
    invoiceFilter: string;
}

export interface InvoicesState {
    entities: { [id: string]: InvoiceDto };
    ids: number[];
    selectedInvoice: number | null;
    areInvoicesLoaded: boolean;
    isLoading: boolean;
    newInvoiceItems: InvoiceItemDto[];
    isVisible: boolean;
    invoiceFilter: string;
}

export const initialState: InvoicesState = {
    entities: {},
    ids: [],
    selectedInvoice: null,
    areInvoicesLoaded: false,
    newInvoiceItems: [],
    isVisible: false,
    isLoading: false,
    invoiceFilter: "Offen",
};

const invoicesReducer = createReducer(
    initialState,
    on(invoicesActions.loadInvoices,
        (state) => ({ ...state, areInvoicesLoaded: false }),
    ),
    on(invoicesActions.loadInvoicesSuccess,
        (state, { invoices }) => ({
            ...state,
            entities: invoices.reduce((acc, invoice) => ({ ...acc, [invoice.invoice_id]: invoice }), {}),
            ids: invoices.map(invoice => invoice.invoice_id),
            areInvoicesLoaded: true
        }),
    ),
    on(invoicesActions.upsertInvoices,
        (state, { invoices }) => {
            const updatedEntities = { ...state.entities };
    
            invoices?.forEach(invoice => {
                updatedEntities[invoice?.invoice_id] = invoice;
            });
    
            const updatedIds = invoices?.map(invoice => invoice?.invoice_id);
    
            return {
                ...state,
                entities: updatedEntities,
                ids: Array.from(new Set([...state.ids, ...updatedIds])),
                areInvoicesLoaded: true
            };
        }
    ),
    on(invoicesActions.deleteInvoiceSuccess,
        (state, { invoiceId }) => {
            const updatedEntities = { ...state.entities };
            delete updatedEntities[invoiceId];
    
            const updatedIds = state.ids.filter(id => id !== invoiceId);
    
            return {
                ...state,
                entities: updatedEntities,
                ids: updatedIds,
                areInvoicesLoaded: true
            };
        }
    ),
    on(invoicesActions.selectInvoice,
        (state, { invoice }) => ({ ...state, selectedInvoice: invoice.invoice_id }),
    ),
    on(invoicesActions.getOpenInvoiceItems,
        (state) => ({ ...state, isLoading: true }),
    ),
    on(invoicesActions.getOpenInvoiceItemsSuccess,
        (state, { invoiceItems }) => ({
            ...state,
            newInvoiceItems: invoiceItems.reduce((acc, item) => {
                const existingItem = acc.find(existing => existing.id === item.id);
                if (existingItem) {
                    return acc.map(existing => existing.id === item.id ? item : existing);
                }
                return [...acc, item];
            }, state.newInvoiceItems),
            isLoading: false
        }),
    ),
    on(invoicesActions.removeOpenInvoiceItem,
        (state, { invoiceItem }) => ({
            ...state,
            newInvoiceItems: state.newInvoiceItems.filter(item => item.id !== invoiceItem.id)
        }),
    ),
    on(invoicesActions.addOrEditOpenInvoiceItem,
        (state, { invoiceItem }) => ({
            ...state,
            newInvoiceItems: state.newInvoiceItems.some(item => item.id === invoiceItem.id)
                ? state.newInvoiceItems.map(item => item.id === invoiceItem.id ? invoiceItem : item)
                : [...state.newInvoiceItems, invoiceItem]
        }),
    ),
    on(invoicesActions.clearOpenInvoiceItems,
        (state) => ({ ...state, newInvoiceItems: [] }),
    ),
    on(invoicesActions.addInvoice,
        (state) => ({ ...state, isLoading: true }),
    ),
    on(invoicesActions.addInvoiceSuccess,
        (state, { invoice }) => ({
            ...state,
            entities: { ...state.entities, [invoice.invoice_id]: invoice },
            ids: [...state.ids, invoice.invoice_id],
            selectedInvoice: invoice.invoice_id,
            isLoading: false
        }),
    ),
    on(errorActions.handleError,
        (state) => ({ ...state, isLoading: false }),
    ),
    on(invoicesActions.createAndSendInvoiceMail,
        (state) => ({ ...state, isLoading: true }),
    ),
    // on(invoicesActions.sendMailSuccess,
    //     (state, { newInvoice }) => ({
    //         ...state,
    //         entities: { ...state.entities, [newInvoice.invoice_id]: newInvoice },
    //         ids: [...state.ids, newInvoice.invoice_id],
    //         selectedInvoice: newInvoice.invoice_id,
    //         isLoading: false
    //     }),
    // ),
    on(invoicesActions.setVisible,
        (state, { isVisible }) => ({ ...state, isVisible }),
    ),
    on(invoicesActions.setInvoiceFilter,
        (state, { invoiceFilter }) => ({ ...state, invoiceFilter }),
    ),
    on(invoicesActions.addInvoiceStatusSuccess,
        (state, { status }) =>({
            ...state,
            entities: {
                ...state.entities,
                [state.selectedInvoice]: {
                    ...state.entities[state.selectedInvoice],
                    invoice_status: [status, ...state.entities[state.selectedInvoice].invoice_status]
                }
            }
        })
    ),
);


export function invoiceReducer(
    state: InvoicesState | undefined,
    action: Action,
) {
    return invoicesReducer(state, action);
}
